import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import '../../styles/sitePages.css';
import './AppDetailPage.css';
import { appStoreUrl, playStoreUrl, trackStoreClick } from '../../utils/storeLinks';

function CrownsAppPage() {
  const reviews = [
    {
      author: 'Google Play Review',
      rating: '★★★★★',
      text: "This game is a lot of fun if you enjoy this style of puzzle. It's an easy game to pick up and play for 5 minutes and walk away from, or you can get caught up in it and spend hours playing levels.",
      source: 'Google Play'
    },
    {
      author: 'App Store Review',
      rating: '★★★★★',
      text: "I absolutely love Crowns! It's the perfect mix of logic and strategy, keeping my mind engaged while being incredibly fun to play.",
      source: 'App Store'
    },
    {
      author: 'App Store Review',
      rating: '★★★★★',
      text: "This game is super fun and well made. Love the conveniences like auto-place x and swipe to mark. I find myself bouncing between game modes, which keeps the game feeling fresh.",
      source: 'App Store'
    }
  ];

  const features = [
    { title: '1,500+ Puzzles', description: 'Hand-crafted levels across multiple difficulty settings to challenge players of all skill levels.' },
    { title: 'Multiple Game Modes', description: 'Classic mode, daily challenges, and puzzle maker to create and share your own levels.' },
    { title: 'Pure Logic Design', description: 'Every puzzle has exactly one solution reachable through logical deduction alone - no guessing required.' },
    { title: 'Clean, Minimal UI', description: 'Distraction-free interface that lets you focus on solving puzzles.' },
    { title: 'Leaderboards', description: 'Compete with players worldwide and track your progress.' },
    { title: 'Offline Play', description: 'No internet required - play anywhere, anytime.' }
  ];

  return (
    <div className="app-detail-page">
      <Helmet>
        <title>Crowns Logic Game - Mobile App for iOS & Android | Logic Puzzles</title>
        <meta name="description" content="Download Crowns Logic Game for iOS and Android. Over 155K downloads and a 4.8-star App Store rating. 1,500+ hand-crafted puzzles with pure logic gameplay." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps/crowns" />
        <meta property="og:title" content="Crowns Logic Game - Mobile App for iOS & Android" />
        <meta property="og:description" content="Download Crowns Logic Game. Over 155K downloads and a 4.8-star App Store rating. 1,500+ hand-crafted puzzles." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps/crowns" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Crowns Logic Game - Mobile App for iOS & Android" />
        <meta name="twitter:description" content="Download Crowns Logic Game. Over 155K downloads and a 4.8-star App Store rating. 1,500+ hand-crafted puzzles." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>

      <NavBar />

      <section className="app-detail-hero">
        <div className="container app-detail-hero-content">
          <div className="app-detail-hero-copy">
            <span className="eyebrow">Mobile app · iOS &amp; Android</span>
            <h1>Crowns Logic Game</h1>
            <p className="app-tagline">The ultimate crown placement puzzle</p>

            <div className="app-detail-stats">
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">4.8 ★</span>
                <span className="app-detail-stat-label">Rating</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">155K+</span>
                <span className="app-detail-stat-label">Downloads</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">1,500+</span>
                <span className="app-detail-stat-label">Puzzles</span>
              </div>
            </div>

            <div className="app-detail-buttons">
              <a href={appStoreUrl('crowns', 'apps-page', '/apps/crowns')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'crowns', store: 'app_store', placement: 'apps-page', page: '/apps/crowns' })}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
              <a href={playStoreUrl('crowns', 'apps-page', '/apps/crowns')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'crowns', store: 'google_play', placement: 'apps-page', page: '/apps/crowns' })}>
                <img src="/play-store-badge.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-detail-hero-art">
            <img
              src="/assets/logos/crowns_logo_256.webp"
              alt="Crowns Logic Game App Icon"
              className="app-detail-icon"
              width="200"
              height="200"
            />
          </div>
        </div>
      </section>

      <div className="app-detail-content">
        <section className="app-detail-section">
          <h2>About Crowns</h2>
          <p className="app-detail-description">
            Crowns is the ultimate puzzle game where each row, column, and color region must contain exactly one crown, 
            and crowns cannot be placed in adjacent cells. With over 1,500 hand-crafted puzzles, multiple game modes, 
            and a puzzle maker to create and share your own levels, Crowns offers endless hours of brain-teasing fun.
          </p>
        </section>

        <section className="app-detail-section">
          <h2>Features</h2>
          <div className="app-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="app-feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>How to Play Crowns</h2>
          <p className="app-detail-description">
            Each Crowns puzzle presents a grid divided into colored regions. Your goal is to place exactly one crown
            in every row, column, and color region. The catch: crowns cannot touch each other, not even diagonally.
            Start by identifying regions or rows where only one cell is possible, then use elimination to work
            through the rest. Every puzzle in the app has exactly one solution reachable through pure logic.
          </p>
          <h3>Tips for New Players</h3>
          <ul className="app-detail-list">
            <li>Look for small regions first - a region with only one or two cells narrows your options quickly.</li>
            <li>Mark cells with an X where a crown cannot go. The app supports swipe-to-mark for fast elimination.</li>
            <li>If a row or column already has a crown, every other cell in that line is eliminated.</li>
            <li>The diagonal rule is powerful - placing one crown eliminates up to eight surrounding cells.</li>
          </ul>
        </section>

        <section className="app-detail-section app-reviews-section">
          <h2>What Players Are Saying</h2>
          <div className="app-reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="app-review-card">
                <div className="app-review-header">
                  <span className="app-review-author">{review.author}</span>
                  <span className="app-review-rating">{review.rating}</span>
                </div>
                <p className="app-review-text">"{review.text}"</p>
                <span className="app-review-source">{review.source}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section app-cta-section sp-cta">
          <div>
            <h2>Ready to Play?</h2>
            <p>Download Crowns now and start your logic puzzle journey!</p>
          </div>
          <div className="app-cta-buttons sp-cta__actions">
            <Link to="/play/crowns" className="button spark">
              Play Free Online
            </Link>
            <Link to="/apps" className="button sp-on-dark">
              ← Back to All Apps
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CrownsAppPage;
